import { QueryFactory } from '@lmt/shared-types';

import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { VisitTypeTable } from '@lmt/unicorn-shared/app/components/visit-types/list/VisitTypeTable';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import { StringParam, withDefault } from 'react-router-url-params';
import { useDebouncedCallback } from 'use-debounce';
import { useEventCallback } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';

export const VisitTypeList = () => {
  const queryClient = useQueryClient();
  const i18next = useScopedTranslation('dictionaries', NS_DataBlockFields);
  const i18nextGlobal = useTranslation();
  const forms = useGlobalForms();
  const dispatch = useDispatch();
  const modals = useModal();
  const visitTypes = useActiveVisitTypes();

  const [queryParams, setQueryParams] = useQueryParamsInTab({
    search: withDefault(StringParam, undefined),
  });

  const openCreate = useEventCallback(() => forms.openVisitTypeCreating());

  const openEdit = useEventCallback((id: number) => {
    forms.openVisitTypeEditing(id);
  });

  const removeVisitType = useEventCallback(async (id: number) => {
    const isConfirmed = await modals.showConfirm({
      text: i18next.t('list.confirm_message_remove'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.VisitTypeClient.remove(id);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.VisitTypeQuery.getQueryKey(),
        });
      } catch (error) {
        await modals.showError({
          text: convertToErrorString(error),
        });
      } finally {
      }
    }
  });

  const onSearchUpdated = useDebouncedCallback((value: string) => {
    setQueryParams({
      search: value,
    });
  }, 300);

  const data = useMemo(() => {
    return queryParams.search
      ? visitTypes.filter((x) =>
          i18nextGlobal
            .t(x.value as any)
            .toLowerCase()
            .includes(queryParams.search!.toLowerCase()),
        )
      : visitTypes;
  }, [visitTypes, queryParams.search]);

  useInsertKeyHandler(openCreate);

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle
          title={i18next.t('visit_types.title')}
          count={visitTypes.length}
        />
        <div className={tableStyles.searchField}>
          <SearchInput
            placeholder={i18next.t('list.search_placeholder')}
            bordered={true}
            onChange={(e) => onSearchUpdated(e.target.value)}
            onSearchIconClick={onSearchUpdated}
            onClear={onSearchUpdated}
            initialValue={queryParams.search}
          />
        </div>
        <div className={tableStyles.filters}>
          <Button
            className={tableStyles.filterItem}
            color={ButtonColor.Primary}
            iconComponent={<AddIcon />}
            onClick={openCreate}
            title={i18next.t('visit_types.add_button')}
            testId={'plus-button'}
          />
        </div>
      </div>

      <div className={tableStyles.tableContainer}>
        <VisitTypeTable
          data={data}
          onEdit={openEdit}
          onDelete={removeVisitType}
        />
      </div>
    </div>
  );
};
